// ClientTrackBoard — board of trackCards for the selected client + week.
//
// Hierarchy enrichment: rows are joined with the governed MarketTrack
// registry via `marketTracksByTrackId` (passed by `ClientView`). A
// matching market track contributes structural metadata only — role
// (`CORE_TRACK` / `WATCHLIST_TRACK` / `SUB_PATTERN` / weekly) and
// parent track id — so a client card flagged as a supporting pattern
// or weekly movement in the registry can nest under its parent card in
// the same client board. Client-specific values (category, urgency,
// relevance, momentum, maturity, position) are NEVER overwritten;
// only the structural display fields are inherited.

import React, { useMemo, useState } from "react";
import {
    CATEGORY_LABEL,
    CATEGORY_TONE,
    humanizeEnumLabel,
    levelLabel,
    levelTone,
    signalStatusLabel,
    sortTrackCards,
    trackCardKey,
    marketMomentumLabel,
    marketMomentumTone,
} from "../../../lib/clients";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const ScoreCell = ({ value }) => {
    const v = typeof value === "number" ? value : null;
    if (v == null) return <span className="text-[hsl(var(--mute))]">—</span>;
    let tone = "text-[hsl(var(--ink))]";
    if (v >= 80) tone = "text-[hsl(var(--accent-ink))] font-semibold";
    else if (v >= 60) tone = "text-[hsl(var(--ink))] font-medium";
    else if (v < 40) tone = "text-[hsl(var(--mute))]";
    return <span className={`num ${tone}`}>{v}</span>;
};

const MomentumBadge = ({ momentum }) => {
    if (!momentum) return null;
    return <Badge tone={marketMomentumTone(momentum)}>{marketMomentumLabel(momentum) || momentum}</Badge>;
};

const ExpandCaret = ({ expanded, onClick, count, testId }) => (
    <button
        type="button"
        data-testid={testId}
        aria-label={expanded ? "Collapse supporting signals" : "Expand supporting signals"}
        aria-expanded={!!expanded}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"
        title={expanded ? "Collapse supporting signals" : `Expand ${count} supporting signal${count === 1 ? "" : "s"}`}
    >
        <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}>▶</span>
    </button>
);

/** Look up the governed registry entry for a card. Joins through the
 *  list of identifiers the publisher pipeline can carry, in priority
 *  order. Returns null if no match exists. */
const findRegistryEntry = (card, registryById) => {
    if (!card || !registryById || !(registryById instanceof Map) || registryById.size === 0) return null;
    const ids = [card.trackId, card.canonicalTrackId, card.stableTrackId, card.marketTrackId, card?.market?.trackId].filter(Boolean);
    for (const id of ids) {
        if (registryById.has(id)) return registryById.get(id);
    }
    return null;
};

/** Derive structural metadata for a card by joining with the governed
 *  registry. Returns `{ role, parentTrackId }` where role is one of:
 *  `CORE_TRACK`, `WATCHLIST_TRACK`, `SUB_PATTERN`, `WEEKLY_DYNAMIC`, or
 *  null if no match. Falls back to card-level fields when the registry
 *  doesn't contain a match. */
const structuralRoleOf = (card, registryById) => {
    const entry = findRegistryEntry(card, registryById);
    if (entry) {
        const g = entry._governed || {};
        if (g.isWeeklyDynamic) return { role: "WEEKLY_DYNAMIC", parentTrackId: g.parentTrackId || entry.ParentTrackID || entry.parentTrackId || null, entry };
        if (g.isSubPattern || entry._role === "SUB_PATTERN") return { role: "SUB_PATTERN", parentTrackId: g.parentTrackId || entry.ParentTrackID || entry.parentTrackId || null, entry };
        if (entry._role === "WATCHLIST_TRACK") return { role: "WATCHLIST_TRACK", parentTrackId: null, entry };
        if (entry._role === "CORE_TRACK") return { role: "CORE_TRACK", parentTrackId: null, entry };
    }
    // No registry match — fall back to whatever the card carries itself.
    const cardRole = (card?.market?.role || "").toUpperCase();
    if (cardRole) return { role: cardRole, parentTrackId: card?.market?.parentTrackId || card?.parentTrackId || null, entry: null };
    return { role: null, parentTrackId: null, entry: null };
};

const isChildRole = (role) => role === "SUB_PATTERN" || role === "WEEKLY_DYNAMIC";

const ChildRoleChip = ({ role }) => {
    if (role === "WEEKLY_DYNAMIC") return <Badge tone="accent">This week&rsquo;s movement</Badge>;
    if (role === "SUB_PATTERN") return <Badge tone="muted">Supporting pattern</Badge>;
    return null;
};

const ParentRoleChip = ({ role }) => {
    if (role === "CORE_TRACK") return <Badge tone="ink">Core signal</Badge>;
    if (role === "WATCHLIST_TRACK") return <Badge tone="warning">Watchlist signal</Badge>;
    return null;
};

const cardMatchesSearch = (card, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [
        card.stableTrackName, card.trackId, card.canonicalTrackId,
        card?.market?.marketZone, card?.marketZone, card?.macroTheme,
        card?.client?.cardCategory,
        card?.client?.relevanceFrontendSummary,
        card?.client?.relevanceExplanation,
        card?.client?.positioningRecommendedNextAction,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
};

const ClientTrackBoard = ({ trackCards, selectedTrackId, onSelect, clientId, selectedWeek, marketTracksByTrackId }) => {
    const [search, setSearch] = useState("");
    const [expandedMap, setExpandedMap] = useState({});
    const toggleExpand = (id) => setExpandedMap((m) => ({ ...m, [id]: !m[id] }));

    const cards = useMemo(() => sortTrackCards(trackCards || []), [trackCards]);

    // Build a quick lookup so the registry join works regardless of which
    // identifier field the client payload carries.
    const registryById = useMemo(() => {
        if (!marketTracksByTrackId) return new Map();
        if (marketTracksByTrackId instanceof Map) return marketTracksByTrackId;
        if (typeof marketTracksByTrackId === "object") return new Map(Object.entries(marketTracksByTrackId));
        return new Map();
    }, [marketTracksByTrackId]);

    // Enrich every card with its structural role + parent. The enrichment
    // is purely metadata — the original card object is preserved on
    // `enriched.card` so the renderer continues to pull client-specific
    // values from there.
    const enriched = useMemo(() => cards.map((card) => {
        const sr = structuralRoleOf(card, registryById);
        return { card, role: sr.role, parentTrackId: sr.parentTrackId, registryEntry: sr.entry };
    }), [cards, registryById]);

    // Map track id → enriched entry for parent resolution.
    const byTrackId = useMemo(() => {
        const m = new Map();
        for (const e of enriched) {
            const ids = [e.card.trackId, e.card.canonicalTrackId].filter(Boolean);
            for (const id of ids) m.set(id, e);
        }
        return m;
    }, [enriched]);

    // Partition into parents (CORE/WATCHLIST or unmatched top-level) and
    // child entries that resolve to a parent present in this client's
    // board. Children whose parent is absent stay as standalone rows
    // labelled "Supporting pattern" or "This week's movement" with a
    // `Parent: <name>` hint so the user never silently loses them.
    const { parents, childrenByParentId, orphans } = useMemo(() => {
        const childMap = new Map();
        const orphanList = [];
        const parentList = [];
        for (const e of enriched) {
            if (isChildRole(e.role) && e.parentTrackId && byTrackId.has(e.parentTrackId)) {
                if (!childMap.has(e.parentTrackId)) childMap.set(e.parentTrackId, []);
                childMap.get(e.parentTrackId).push(e);
            } else if (isChildRole(e.role)) {
                orphanList.push(e); // child with no parent in this board
            } else {
                parentList.push(e); // CORE/WATCHLIST or unknown — top-level
            }
        }
        return { parents: parentList, childrenByParentId: childMap, orphans: orphanList };
    }, [enriched, byTrackId]);

    // Search: a parent is visible if it matches, OR if any of its
    // children match (and we auto-expand it).
    const visibleParents = useMemo(() => {
        if (!search.trim()) return parents;
        const q = search.trim();
        return parents.filter((p) => {
            if (cardMatchesSearch(p.card, q)) return true;
            const kids = childrenByParentId.get(p.card.trackId) || childrenByParentId.get(p.card.canonicalTrackId) || [];
            return kids.some((c) => cardMatchesSearch(c.card, q));
        });
    }, [parents, childrenByParentId, search]);

    const autoExpandedSet = useMemo(() => {
        if (!search.trim()) return new Set();
        const q = search.trim();
        const set = new Set();
        for (const p of parents) {
            const parentMatches = cardMatchesSearch(p.card, q);
            if (parentMatches) continue;
            const kids = childrenByParentId.get(p.card.trackId) || childrenByParentId.get(p.card.canonicalTrackId) || [];
            if (kids.some((c) => cardMatchesSearch(c.card, q))) {
                set.add(p.card.trackId);
                if (p.card.canonicalTrackId) set.add(p.card.canonicalTrackId);
            }
        }
        return set;
    }, [parents, childrenByParentId, search]);

    const visibleOrphans = useMemo(() => {
        if (!search.trim()) return orphans;
        const q = search.trim();
        return orphans.filter((o) => cardMatchesSearch(o.card, q));
    }, [orphans, search]);

    if (cards.length === 0) {
        return (
            <div className="card-flat p-8 text-center text-sm text-[hsl(var(--mute))]" data-testid="client-track-board-empty">
                No approved signals for this week.
            </div>
        );
    }

    const renderRow = (entry, opts = {}) => {
        const { isChild = false, parentName = null } = opts;
        const { card, role } = entry;
        const cat = (card?.client?.cardCategory || "").toUpperCase();
        const tone = CATEGORY_TONE[cat] || "muted";
        const selected = card.trackId === selectedTrackId;
        const rowKey = trackCardKey(card, { clientId, selectedWeek });
        const signalName = card.stableTrackName || card.trackId;
        const hasChildren = !isChild && (childrenByParentId.get(card.trackId)?.length || childrenByParentId.get(card.canonicalTrackId)?.length);
        const expanded = !isChild && (expandedMap[card.trackId] || expandedMap[card.canonicalTrackId] || autoExpandedSet.has(card.trackId) || autoExpandedSet.has(card.canonicalTrackId));
        const totalKids = hasChildren ? (childrenByParentId.get(card.trackId)?.length || childrenByParentId.get(card.canonicalTrackId)?.length) : 0;

        return (
            <tr
                key={rowKey}
                data-testid={`client-track-row-${card.trackId}`}
                onClick={() => onSelect && onSelect(card.trackId)}
                className={`border-t border-[hsl(var(--line))] cursor-pointer transition-colors ${selected ? "bg-[hsl(var(--accent-soft))] shadow-[inset_3px_0_0_0_hsl(var(--accent))]" : `hover:bg-[hsl(var(--surface-sunken))] ${isChild ? "bg-[hsl(var(--surface-sunken))]/40" : ""}`}`}
            >
                <td className="px-2 py-3 align-top w-6">
                    {hasChildren ? (
                        <ExpandCaret
                            expanded={expanded}
                            onClick={() => {
                                const k = card.trackId || card.canonicalTrackId;
                                if (k) toggleExpand(k);
                            }}
                            count={totalKids}
                            testId={`client-expand-${card.trackId}`}
                        />
                    ) : null}
                </td>
                <td className="px-3 py-3 align-top max-w-[420px]">
                    <div className={isChild ? "pl-4 border-l-2 border-[hsl(var(--line))]" : ""}>
                        <div className={`leading-snug ${isChild ? "text-[13px] text-[hsl(var(--ink-soft))]" : "text-sm font-medium text-[hsl(var(--ink))]"}`}>{signalName}</div>
                        <div className="text-[10px] text-[hsl(var(--mute))] mt-0.5 flex items-center gap-1.5 flex-wrap">
                            {isChild && <ChildRoleChip role={role} />}
                            {!isChild && <ParentRoleChip role={role} />}
                            {parentName && <span>· Parent: {parentName}</span>}
                            {!isChild && hasChildren && !expanded && (
                                <span className="num">· {totalKids} supporting</span>
                            )}
                        </div>
                    </div>
                </td>
                <td className="px-3 py-3 align-top"><Badge tone={tone}>{CATEGORY_LABEL[cat] || card?.client?.cardCategory || "—"}</Badge></td>
                <td className="px-3 py-3 align-top text-right"><ScoreCell value={card?.client?.relevanceScore} /></td>
                <td className="px-3 py-3 align-top"><Badge tone={levelTone(card?.client?.urgency)}>{levelLabel(card?.client?.urgency)}</Badge></td>
                <td className="px-3 py-3 align-top">
                    <div className="flex items-center gap-2">
                        <ScoreCell value={card?.market?.score} />
                        <MomentumBadge momentum={card?.market?.momentum} />
                    </div>
                </td>
                <td className="px-3 py-3 align-top text-[hsl(var(--ink-soft))]" data-testid={`maturity-${card.trackId}`}>{signalStatusLabel(card?.market?.maturityStage) || "—"}</td>
                <td className="px-3 py-3 align-top text-[hsl(var(--ink-soft))]">{humanizeEnumLabel(card?.client?.position, "clientPositioning") || "—"}</td>
            </tr>
        );
    };

    return (
        <div className="card-flat overflow-hidden" data-testid="client-track-board">
            <header className="px-5 py-4 border-b border-[hsl(var(--line))]">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="eyebrow">Client signal board</div>
                        <h2 className="display text-xl mt-1">Market signals ranked by client relevance</h2>
                        <p className="text-[11px] text-[hsl(var(--mute))] mt-1">Sorted by signal category, client relevance, and urgency. Supporting patterns and this week&rsquo;s movements nest beneath their parent signal.</p>
                    </div>
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search signals…"
                        data-testid="client-track-search"
                        className="px-3 py-1.5 text-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded min-w-[200px]"
                    />
                </div>
            </header>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px]">
                            <th className="px-2 py-3 w-6"></th>
                            <th className="px-3 py-3 text-left">Signal</th>
                            <th className="px-3 py-3 text-left">Category</th>
                            <th className="px-3 py-3 text-right">Relevance</th>
                            <th className="px-3 py-3 text-left">Urgency</th>
                            <th className="px-3 py-3 text-left">Market momentum</th>
                            <th className="px-3 py-3 text-left">Maturity</th>
                            <th className="px-3 py-3 text-left">Position</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleParents.map((p) => {
                            const kids = childrenByParentId.get(p.card.trackId) || childrenByParentId.get(p.card.canonicalTrackId) || [];
                            const expanded = expandedMap[p.card.trackId] || expandedMap[p.card.canonicalTrackId] || autoExpandedSet.has(p.card.trackId) || autoExpandedSet.has(p.card.canonicalTrackId);
                            const q = search.trim();
                            const renderKids = expanded ? (q ? kids.filter((c) => cardMatchesSearch(c.card, q) || cardMatchesSearch(p.card, q)) : kids) : [];
                            return (
                                <React.Fragment key={trackCardKey(p.card, { clientId, selectedWeek })}>
                                    {renderRow(p)}
                                    {renderKids.map((c) => renderRow(c, { isChild: true }))}
                                </React.Fragment>
                            );
                        })}
                        {visibleOrphans.map((o) => {
                            // Child whose parent is NOT in this board. Show as
                            // standalone with a clear "Parent: <name>" hint
                            // using the registry's parent name when available.
                            const parentEntry = o.parentTrackId && registryById.get(o.parentTrackId);
                            const parentName = parentEntry?.StableTrackName || parentEntry?.SuggestedDisplayLabel || null;
                            return (
                                <React.Fragment key={trackCardKey(o.card, { clientId, selectedWeek }) + "-orphan"}>
                                    {renderRow(o, { isChild: true, parentName })}
                                </React.Fragment>
                            );
                        })}
                        {visibleParents.length === 0 && visibleOrphans.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[hsl(var(--mute))]">No signals match the current search.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientTrackBoard;
